/**
 * Offline Storage Utility
 * Handles IndexedDB operations for offline data persistence
 */

const DB_NAME = 'pivot-offline';
const DB_VERSION = 3;
const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Store names
const STORES = {
  WORKOUTS: 'offline-workouts',
  JUMPS: 'offline-jumps',
  PROGRESS: 'offline-progress',
  USER_DATA: 'user-data',
  DRILLS: 'offline-drills'
};

// Open IndexedDB connection
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create object stores if they don't exist
      Object.values(STORES).forEach(storeName => {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
        }
      });
    };
  });
}

// Save data to IndexedDB
export async function saveOfflineData(storeName, data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const item = {
      ...data,
      timestamp: Date.now(),
      token: localStorage.getItem('token')
    };
    
    const request = store.add(item);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

// Get all data from a store
export async function getOfflineData(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

// Delete specific item
export async function deleteOfflineData(storeName, id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

// Clear all data in a store
export async function clearOfflineStore(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

// Save workout offline
export async function saveWorkoutOffline(workoutData) {
  return saveOfflineData(STORES.WORKOUTS, { data: workoutData });
}

// Save jump offline
export async function saveJumpOffline(jumpData) {
  return saveOfflineData(STORES.JUMPS, { data: jumpData });
}

// Save progress offline
export async function saveProgressOffline(progressData) {
  return saveOfflineData(STORES.PROGRESS, { data: progressData });
}

// Get pending offline items count
export async function getPendingSyncCount() {
  const [workouts, jumps, progress] = await Promise.all([
    getOfflineData(STORES.WORKOUTS),
    getOfflineData(STORES.JUMPS),
    getOfflineData(STORES.PROGRESS)
  ]);
  
  return workouts.length + jumps.length + progress.length;
}

// Request background sync
export async function requestSync(tag) {
  if ('serviceWorker' in navigator && 'sync' in window.registration) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register(tag);
      console.log(`[Offline] Registered sync: ${tag}`);
    } catch (error) {
      console.error('[Offline] Sync registration failed:', error);
    }
  }
}

// Sync workouts when online
export async function syncWorkouts() {
  if (!navigator.onLine) return;
  
  const workouts = await getOfflineData(STORES.WORKOUTS);
  
  for (const workout of workouts) {
    try {
      const response = await fetch(`${API_BASE}/workouts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${workout.token}`
        },
        body: JSON.stringify(workout.data)
      });
      
      if (response.ok) {
        await deleteOfflineData(STORES.WORKOUTS, workout.id);
        console.log('[Offline] Synced workout:', workout.id);
      }
    } catch (error) {
      console.error('[Offline] Failed to sync workout:', error);
    }
  }
}

// Sync jumps when online
export async function syncJumps() {
  if (!navigator.onLine) return;
  
  const jumps = await getOfflineData(STORES.JUMPS);
  
  for (const jump of jumps) {
    try {
      const response = await fetch(`${API_BASE}/jumps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jump.token}`
        },
        body: JSON.stringify(jump.data)
      });
      
      if (response.ok) {
        await deleteOfflineData(STORES.JUMPS, jump.id);
        console.log('[Offline] Synced jump:', jump.id);
      }
    } catch (error) {
      console.error('[Offline] Failed to sync jump:', error);
    }
  }
}

// Sync all pending data
export async function syncAll() {
  if (!navigator.onLine) return;
  
  await Promise.all([
    syncWorkouts(),
    syncJumps()
  ]);
}

// ===== Offline-first POST wrappers =====
// Try network first; if it fails, queue to IndexedDB for later sync.

export async function postWorkoutWithOfflineFallback(workoutData, token) {
  if (navigator.onLine) {
    try {
      const res = await fetch(`${API_BASE}/workouts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(workoutData)
      });
      if (res.ok) return { synced: true, data: await res.json() };
    } catch (e) {
      console.warn('[Offline] Workout POST failed, queuing for sync:', e.message);
    }
  }
  await saveWorkoutOffline(workoutData);
  return { synced: false, queued: true };
}

export async function postJumpWithOfflineFallback(jumpData, token) {
  if (navigator.onLine) {
    try {
      const res = await fetch(`${API_BASE}/jumps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(jumpData)
      });
      if (res.ok) return { synced: true, data: await res.json() };
    } catch (e) {
      console.warn('[Offline] Jump POST failed, queuing for sync:', e.message);
    }
  }
  await saveJumpOffline(jumpData);
  return { synced: false, queued: true };
}

// ===== Drill pre-cache for offline (Feature D) =====

// Save a drill detail to IndexedDB (keyed by drill ID, not auto-increment).
async function putDrill(drill) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.DRILLS, 'readwrite');
    const store = tx.objectStore(STORES.DRILLS);
    const req = store.put({ ...drill, cached_at: Date.now() });
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
}

export async function getCachedDrills() {
  return getOfflineData(STORES.DRILLS);
}

export async function getCachedDrill(drillId) {
  const all = await getCachedDrills();
  return all.find(d => d.id === drillId) || null;
}

/**
 * Downloads all drills + their manga images to the device for offline use.
 * onProgress({ current, total, phase }) — called as it iterates.
 */
export async function downloadDrillsForOffline(token, onProgress = () => {}) {
  // Step 1: fetch drill list
  onProgress({ current: 0, total: 0, phase: 'fetching_list' });
  const listRes = await fetch(`${API_BASE}/drills`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!listRes.ok) throw new Error('Could not fetch drills list');
  const drills = await listRes.json();

  // Step 2: fetch each drill's full detail (includes steps_manga) + cache it
  const total = drills.length;
  let done = 0;
  const imageUrls = new Set();

  for (const drill of drills) {
    try {
      const detailRes = await fetch(`${API_BASE}/drills/${drill.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (detailRes.ok) {
        const detail = await detailRes.json();
        await putDrill(detail);

        // Collect manga image URLs to pre-cache
        if (detail.illustration_url) imageUrls.add(detail.illustration_url);
        if (detail.image_url) imageUrls.add(detail.image_url);
        if (Array.isArray(detail.steps_manga)) {
          detail.steps_manga.forEach(s => {
            if (s?.image) imageUrls.add(s.image);
          });
        }
      }
    } catch (e) {
      console.warn('[Offline] Skipping drill', drill.id, e);
    }
    done++;
    onProgress({ current: done, total, phase: 'caching_drills' });
  }

  // Step 3: pre-cache all manga images via the Service Worker cache
  const imgArray = Array.from(imageUrls);
  if ('caches' in window) {
    try {
      const cache = await caches.open('pivot-manga-v1');
      let imgDone = 0;
      for (const url of imgArray) {
        try {
          const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
          const resp = await fetch(fullUrl);
          if (resp.ok) await cache.put(fullUrl, resp.clone());
        } catch (e) {
          console.warn('[Offline] Skipping image cache:', url, e.message);
        }
        imgDone++;
        onProgress({ current: imgDone, total: imgArray.length, phase: 'caching_images' });
      }
    } catch (e) {
      console.warn('[Offline] Image caching failed:', e);
    }
  }

  // Track completion
  localStorage.setItem('pivot_offline_pack_at', new Date().toISOString());
  localStorage.setItem('pivot_offline_pack_count', String(drills.length));

  onProgress({ current: total, total, phase: 'complete' });
  return { drills: drills.length, images: imgArray.length };
}

export function getOfflinePackInfo() {
  return {
    cachedAt: localStorage.getItem('pivot_offline_pack_at'),
    drillCount: parseInt(localStorage.getItem('pivot_offline_pack_count') || '0', 10)
  };
}

export async function clearOfflinePack() {
  await clearOfflineStore(STORES.DRILLS);
  if ('caches' in window) {
    try { await caches.delete('pivot-manga-v1'); } catch (e) { console.warn('[Offline] Could not delete manga cache:', e.message); }
  }
  localStorage.removeItem('pivot_offline_pack_at');
  localStorage.removeItem('pivot_offline_pack_count');
}

// Export store names for use elsewhere
export { STORES };
